/**
 * @author       luis.orihuela@agileengine.com (Luis Orihuela)
 * @copyright    Copyright (c) 2022, Zumper
 */

import { useEffect, useRef, useState, SetStateAction } from 'react'

export const useLocalStorageState = <T>(
  key: string,
  defaultValue?: T
): [typeof state, SetStateAction<any>] => {
  // hold a ref to the current key
  const prevKeyRef = useRef(key)
  const [state, setState] = useState<T>(() => {
    // NOTE: jest doesn't let you set global to undefined, so there's no test for the SSR case
    /* istanbul ignore next */
    if (typeof window === 'undefined') {
      return typeof defaultValue === 'function' ? defaultValue() : defaultValue
    }
    const valueInLocalStorage = window.localStorage.getItem(key)

    if (valueInLocalStorage) {
      return JSON.parse(valueInLocalStorage)
    }
    return typeof defaultValue === 'function' ? defaultValue() : defaultValue
  })

  useEffect(() => {
    /* istanbul ignore next */
    if (typeof window === 'undefined') {
      return
    }
    const prevKey = prevKeyRef.current
    // if the key changed, remove the previous key
    if (prevKey !== key) {
      window.localStorage.removeItem(key)
    }
    prevKeyRef.current = key
    window.localStorage.setItem(key, JSON.stringify(state))
  }, [key, state])

  return [state, setState]
}
